import { useCallback } from "react";

import { Colors, FormGroup, InputGroup } from "@blueprintjs/core";
import { Meta, UppyFile } from "@uppy/core";

import { Box, Flex } from "@/components/layout/flexbox";
import { PersonalityTraitSelector } from "@/components/pages_logged_in/roleplays/details/scenario/edit/PersonalityTraitsSelector";
import { VoiceSelector } from "@/components/pages_logged_in/roleplays/details/scenario/edit/VoiceSelector";
import { Tiptap } from "@/components/pieces/editor/tiptap";
import { BulletPointInput } from "@/components/pieces/form/BulletPointInput";
import { Clickable } from "@/components/pieces/interaction/Clickable";
import { UploaderProps } from "@/components/pieces/uploader";
import {
  ButtonPosition,
  ImageFrameType,
  UploadImageController,
} from "@/components/pieces/uploader/UploadImageController";
import {
  BRAND_PURPLE,
  LIGHTEST_BACKGROUND,
  SUBTEXT_COLOR,
} from "@/css/constants";
import { uploadFile } from "@/lib/upload-file";

import { action, state } from "./useEditScenario";

export function CharacterTab({
  persona,
  dispatch,
  photoEndpoint,
}: {
  persona: state["persona"];
  dispatch: (action: action) => void;
  photoEndpoint: string;
}) {
  const scenarioVariables = {
    first_name: persona.firstName || "First Name",
    last_name: persona.lastName || "Last Name",
    name: persona.name || "Name",
    company: persona.company || "Company",
    job_title: persona.jobTitle || "Job Title",
  };
  const handlePersonaNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch({ type: "setPersonaName", name: event.target.value });
    },
    [dispatch],
  );
  const handlePersonalityTraitsChange = useCallback(
    (traits: { [key: string]: string }) => {
      dispatch({ type: "setPersonalityTraits", traits: traits });
    },
    [dispatch],
  );
  const handleOpeningLineChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch({ type: "setOpeningLine", openingLine: event.target.value });
    },
    [dispatch],
  );
  const handleVoiceIdChange = useCallback(
    (voiceId: string) => {
      dispatch({ type: "setVoiceId", voiceId: voiceId });
    },
    [dispatch],
  );
  const handlePersonalityCharacteristicsChange = useCallback(
    (characteristics: string[]) => {
      dispatch({
        type: "setPersonalityCharacteristics",
        characteristics: characteristics,
      });
    },
    [dispatch],
  );
  const handlePhotoUpload: UploaderProps["onUploadSuccess"] = useCallback(
    async (
      _file: UppyFile<Meta, Record<string, never>> | undefined,
      response: {
        body?: Record<string, unknown>;
        status: number;
        bytesUploaded?: number;
        uploadURL?: string;
      },
    ) => {
      dispatch({ type: "setPhoto", photo: response.uploadURL ?? "" });
    },
    [dispatch],
  );
  const handleJobTitleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch({ type: "setJobTitle", jobTitle: event.target.value });
    },
    [dispatch],
  );
  const handleCompanyChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch({ type: "setCompany", company: event.target.value });
    },
    [dispatch],
  );
  const handleBackstoryChange = useCallback(
    (value: string) => {
      dispatch({ type: "setBackstory", backstory: value });
    },
    [dispatch],
  );

  const handleSituationalUnderstandingChange = useCallback(
    (value: string) => {
      dispatch({
        type: "setSituationalUnderstanding",
        situationalUnderstanding: value,
      });
    },
    [dispatch],
  );
  return (
    <Flex
      flexDirection="column"
      p={24}
      pb={12}
      backgroundColor={LIGHTEST_BACKGROUND}
      className="flex-auto"
    >
      <Box fontSize={18} fontWeight={600}>
        Basic Identity
      </Box>
      <Box
        backgroundColor={Colors.WHITE}
        p={3}
        className="bas round-corners"
        mt={2}
        mb={24}
      >
        <Flex mb={24}>
          <UploadImageController
            logoSrc={persona.photo ?? ""}
            endpoint={photoEndpoint}
            fieldName="photo"
            imagesSize={150}
            buttonLabel="Change Photo"
            onUploadSuccess={handlePhotoUpload}
            imageFrameType={ImageFrameType.Circle}
            buttonPosition={ButtonPosition.Top}
          />
        </Flex>
        <Box mb={24}>
          <Box fontWeight={600} mb={12}>
            Name
          </Box>
          <InputGroup
            onChange={handlePersonaNameChange}
            value={persona.name}
            large={true}
            fill={true}
            placeholder="Persona Name"
            autoFocus={true}
          />
        </Box>
        <Box mb={24}>
          <Box fontWeight={600} mb={12}>
            Job Title
          </Box>
          <InputGroup
            onChange={handleJobTitleChange}
            value={persona.jobTitle}
            large={true}
            fill={true}
            placeholder="Job Title"
            autoFocus={true}
          />
        </Box>
        <Box mb={24}>
          <Box fontWeight={600} mb={12}>
            Company
          </Box>
          <InputGroup
            onChange={handleCompanyChange}
            value={persona.company}
            large={true}
            fill={true}
            placeholder="Company"
            autoFocus={true}
          />
        </Box>
        <Box>
          <Box fontWeight={600} mb={12}>
            Voice
          </Box>
          <VoiceSelector
            voiceId={persona.voiceId}
            onChangeVoiceId={handleVoiceIdChange}
          />
        </Box>
      </Box>
      <FormGroup
        label={
          <Box fontSize={18} mb={1} fontWeight={600}>
            Opening Line
          </Box>
        }
        labelInfo={
          <Box fontSize={14} fontWeight={400} color={SUBTEXT_COLOR}>
            This is the first thing that the AI says when the conversation
            starts.
          </Box>
        }
      >
        <Box mt={2}>
          <InputGroup
            large={true}
            value={persona.openingLine}
            onChange={handleOpeningLineChange}
          />
        </Box>
      </FormGroup>

      <FormGroup
        label={
          <Box fontSize={18} mb={1} fontWeight={600}>
            Character Backstory
          </Box>
        }
        labelInfo={
          <Box fontSize={14} fontWeight={400} color={SUBTEXT_COLOR}>
            Provide a detailed background story for the character (not shown to
            users)
          </Box>
        }
      >
        <Flex mt={2} backgroundColor={Colors.WHITE} className="round-corners">
          <Tiptap
            content={persona.backstory ?? ""}
            onChange={handleBackstoryChange}
            onFileUpload={uploadFile({ isPrivate: false })}
            variables={scenarioVariables}
          />
        </Flex>
      </FormGroup>

      <FormGroup
        label={
          <Box fontSize={18} mb={1} fontWeight={600}>
            Situational Understanding
          </Box>
        }
        labelInfo={
          <Box fontSize={14} fontWeight={400} color={SUBTEXT_COLOR}>
            Describe what the character knows about the current situation (not
            shown to users)
          </Box>
        }
      >
        <Flex mt={2} backgroundColor={Colors.WHITE} className="round-corners">
          <Tiptap
            content={persona.situationalUnderstanding ?? ""}
            onChange={handleSituationalUnderstandingChange}
            onFileUpload={uploadFile({ isPrivate: false })}
            variables={scenarioVariables}
          />
        </Flex>
      </FormGroup>

      <FormGroup
        label={
          <Box fontSize={18} mb={1} fontWeight={600}>
            Personality
          </Box>
        }
        labelInfo={
          <Box fontSize={14} fontWeight={400} color={SUBTEXT_COLOR}>
            Context for AI behavior (not shown to users)
          </Box>
        }
      >
        {persona.isTraitSelector ? (
          <Box mt={2}>
            <PersonalityTraitSelector
              personalityTraits={persona.personalityTraits}
              onChangePersonalityTraits={handlePersonalityTraitsChange}
            />
          </Box>
        ) : (
          <Box mt={2}>
            <Box mb={2}>
              This character is using a custom personality description. You can
              edit it directly or{" "}
              <Clickable
                onClick={() => dispatch({ type: "switchToStructured" })}
              >
                <span style={{ color: BRAND_PURPLE }}>
                  switch to using structured traits
                </span>
              </Clickable>
              .
            </Box>
            <BulletPointInput
              values={persona.personalityCharacteristics ?? []}
              onChange={handlePersonalityCharacteristicsChange}
            />
          </Box>
        )}
      </FormGroup>
    </Flex>
  );
}
